import { Builder } from '@builder.io/react'
import React from 'react'

import { HeadingClass, HeadingType } from 'enums'
import getAlignmentClass, { Alignment, textAlignmentProps } from '../../util/TextAlignment'
import CustomHeading, { headingTypePropObj, headingClassPropObj } from '../common/heading'

interface Feature {
  name: string
  description: string
  imageSrc: any
  imageAlt: string
}

interface FeatureBarProp {
  backgroundColor: string
  headingText: string
  headingTextColor: string
  headingSubText: string
  headingSubTextColor: string
  headingSubTextEnabled: boolean
  features: Feature[]
  featureDescriptionEnabled: boolean
  featureTextColor: string
  featureDescriptionColor: string
  headingType: HeadingType
  headingClass: HeadingClass
  headingTextAlignment: Alignment
  featureTextBeyondInfinity: boolean
}

// https://tailwindui.com/components/ecommerce/components/product-features#component-4f0dd8497283578821e874e83a2de3a1
const FeatureBar = ({
  backgroundColor,
  headingText,
  headingSubText,
  headingTextColor,
  headingSubTextColor,
  headingSubTextEnabled,
  features,
  featureDescriptionEnabled,
  featureTextColor,
  featureDescriptionColor,
  headingType,
  headingClass,
  headingTextAlignment,
  featureTextBeyondInfinity
}: FeatureBarProp) => {
  const renderSubHeadingText = () => {
    if (!headingSubTextEnabled) return null

    return (
      <p
        className={`mt-4 text-gray-500 ${getAlignmentClass(headingTextAlignment)}`}
        style={{ color: headingSubTextColor }}
      >
        {headingSubText}
      </p>
    )
  }

  const renderFeatureDescription = (description: string) => {
    if (!featureDescriptionEnabled) return null

    return (
      <p className="mt-2 text-sm text-gray-500" style={{ color: featureDescriptionColor }}>
        {description}
      </p>
    )
  }

  return (
    <div className="bg-white" style={{ backgroundColor }}>
      <div className="mx-auto max-w-2xl py-5 px-4 sm:py-10 sm:px-6 lg:max-w-7xl lg:px-8">
        <div className="w-full">
          <CustomHeading
            classList={`mt-2 tracking-tight text-gray-900 ${getAlignmentClass(headingTextAlignment)}`}
            headingClass={headingClass}
            headingType={headingType}
            color={headingTextColor}
          >
            {headingText}
          </CustomHeading>
          {renderSubHeadingText()}
        </div>

        <div className="mt-11 flex w-full flex-wrap items-start justify-center gap-y-16 gap-x-6 sm:mt-16 lg:gap-x-8">
          {features?.map((feature, i) => (
            <div key={i} className="flex w-full flex-col-reverse sm:w-[calc(50%-1rem)] lg:w-[calc(25%-2rem)]">
              <div className="mt-6">
                <h3
                  className={`font-bold text-gray-900 ${
                    featureTextBeyondInfinity ? 'font-third text-4xl font-normal' : 'text-lg'
                  }`}
                  style={{ color: featureTextColor }}
                >
                  {feature.name}
                </h3>
                {renderFeatureDescription(feature.description)}
              </div>
              <div className="aspect-w-1 aspect-h-1 overflow-hidden bg-gray-100">
                <img
                  src={
                    feature.imageSrc || 'https://tailwindui.com/img/ecommerce-images/product-feature-08-detail-01.jpg'
                  }
                  alt={feature.imageAlt}
                  className="object-cover object-center"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default FeatureBar

Builder.registerComponent(FeatureBar, {
  name: 'FeatureBar',
  inputs: [
    {
      name: 'backgroundColor',
      type: 'color',
      defaultValue: 'green'
    },
    {
      ...headingTypePropObj
    },
    {
      ...headingClassPropObj
    },
    {
      name: 'headingText',
      type: 'text',
      defaultValue: 'Simple Item'
    },
    {
      ...textAlignmentProps,
      name: 'headingTextAlignment'
    },
    {
      name: 'headingSubText',
      type: 'text',
      defaultValue: 'Your Dog is everything. Take care of your Dog.'
    },
    {
      name: 'headingTextColor',
      type: 'color',
      defaultValue: 'black'
    },
    {
      name: 'headingSubTextColor',
      type: 'color',
      defaultValue: 'black'
    },
    {
      name: 'headingSubTextEnabled',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'features',
      type: 'list',
      defaultValue: [{ name: 'Good Food', description: 'Made from scratch', imageSrc: null, imageAlt: 'Our food' }],
      subFields: [
        { name: 'name', type: 'string', defaultValue: 'Good Food' },
        { name: 'description', type: 'string', defaultValue: 'Made from scratch' },
        { name: 'imageSrc', type: 'file', defaultValue: null, allowedFileTypes: ['jpeg', 'png', 'svg'] },
        { name: 'imageAlt', type: 'string', defaultValue: 'Our food' }
      ]
    },
    {
      name: 'featureTextColor',
      type: 'color',
      defaultValue: 'black'
    },
    {
      name: 'featureTextBeyondInfinity',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'featureDescriptionEnabled',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'featureDescriptionColor',
      type: 'color',
      defaultValue: 'black'
    }
  ]
})
